import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import './OnboardingChecklist.css';

const IP = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const Icon = {
  Globe:     (p) => (<svg width="14" height="14" {...IP} {...p}><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15 15 0 0 1 4 10 15 15 0 0 1-4 10 15 15 0 0 1-4-10 15 15 0 0 1 4-10z" /></svg>),
  Link:      (p) => (<svg width="14" height="14" {...IP} {...p}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>),
  Chart:     (p) => (<svg width="14" height="14" {...IP} {...p}><path d="M3 3v18h18" /><path d="m7 14 4-4 4 4 5-5" /></svg>),
  Bolt:      (p) => (<svg width="14" height="14" {...IP} {...p}><path d="M13 2 3 14h9l-1 8 10-12h-9z" /></svg>),
  Users:     (p) => (<svg width="14" height="14" {...IP} {...p}><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>),
  Book:      (p) => (<svg width="14" height="14" {...IP} {...p}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>),
  Check:     (p) => (<svg width="14" height="14" {...IP} strokeWidth="2.5" {...p}><polyline points="20 6 9 17 4 12" /></svg>),
  ArrowRight:(p) => (<svg width="12" height="12" {...IP} {...p}><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>),
  Close:     (p) => (<svg width="12" height="12" {...IP} {...p}><path d="M18 6 6 18M6 6l12 12" /></svg>),
};

const ITEMS = [
  { key: 'connected_domain',   Icon: Icon.Globe, label: 'Connect a domain',      path: '/domains',      accent: 'coral'   },
  { key: 'first_link_created', Icon: Icon.Link,  label: 'Create your first link', path: '/links?new=1',  accent: 'mustard' },
  { key: 'viewed_analytics',   Icon: Icon.Chart, label: 'Explore analytics',      path: '/dashboard',    accent: 'olive'   },
  { key: 'viewed_events',      Icon: Icon.Bolt,  label: 'Explore events',         path: '/events',       accent: 'coral'   },
  { key: 'invited_member',     Icon: Icon.Users, label: 'Invite a team member',   path: '/settings',     accent: 'olive'   },
  { key: 'viewed_docs',        Icon: Icon.Book,  label: 'Read the docs',          path: null,            accent: 'mustard' },
];

export default function OnboardingChecklist() {
  const { workspace, setWorkspace } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  const onboarding = workspace?.onboarding || {};
  const done = ITEMS.filter((i) => onboarding[i.key]).length;
  const pct = Math.round((done / ITEMS.length) * 100);
  const complete = pct === 100;

  if (dismissed || (complete && onboarding.completed_at)) return null;

  const handleItemClick = async (item) => {
    if (onboarding[item.key]) return;
    if (workspace?.workspace_id) {
      const updated = { ...(workspace.onboarding || {}), [item.key]: true };
      const { data } = await supabase
        .from('workspaces')
        .update({ onboarding: updated })
        .eq('workspace_id', workspace.workspace_id)
        .select()
        .single();
      if (data) setWorkspace(data);
    }
    if (item.key === 'viewed_docs') {
      window.open('https://docs.linkflow.app', '_blank');
    } else if (item.path) {
      navigate(item.path);
    }
  };

  return (
    <div className="lfo-root" data-testid="onboarding-checklist">
      <button
        type="button"
        className="lfo-dismiss"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss checklist"
      >
        <Icon.Close />
      </button>

      <div className="lfo-head">
        <div>
          <div className="lfo-label">Welcome · {done}/{ITEMS.length} steps complete</div>
          <div className="lfo-title">
            Get set up in <em>under 3 minutes</em>.
          </div>
        </div>
        <div className="lfo-pct">
          <div className="lfo-pct-num">{pct}<span>%</span></div>
        </div>
      </div>

      <div className="lfo-progress">
        <div className="lfo-progress-fill" style={{ width: `${pct}%` }} />
      </div>

      <div className="lfo-grid">
        {ITEMS.map((item) => {
          const { key, Icon: ItemIcon, label, accent } = item;
          const isDone = !!onboarding[key];
          return (
            <button
              key={key}
              type="button"
              onClick={() => handleItemClick(item)}
              disabled={isDone}
              className={`lfo-item ${isDone ? 'lfo-done' : `lfo-accent-${accent}`}`}
              data-testid={`onboarding-step-${key}`}
            >
              <div className="lfo-item-icon">
                {isDone ? <Icon.Check /> : <ItemIcon />}
              </div>
              <span className="lfo-item-label">{label}</span>
              {isDone ? (
                <span className="lfo-item-done-mark">Done</span>
              ) : (
                <span className="lfo-item-arrow"><Icon.ArrowRight /></span>
              )}
            </button>
          );
        })}
      </div>

      {complete && (
        <div className="lfo-celebrate">
          All steps complete — nice work. Close this when you're ready.
        </div>
      )}
    </div>
  );
}
